'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type OutputFormat = 'jpeg' | 'webp' | 'png';
type CompressionPreset = {
  id: string;
  label: string;
  output: OutputFormat;
  quality: number;
  maxWidth: number;
  hint: string;
};

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

const presets: CompressionPreset[] = [
  {
    id: 'balanced-jpg',
    label: 'Balanced JPG',
    output: 'jpeg',
    quality: 0.82,
    maxWidth: 2400,
    hint: 'A good default when you want a smaller file without making the image feel overly processed.',
  },
  {
    id: 'web-light',
    label: 'WebP light',
    output: 'webp',
    quality: 0.8,
    maxWidth: 2000,
    hint: 'Best when you want a lighter web-ready file and broad modern browser support.',
  },
  {
    id: 'upload-safe',
    label: 'Upload safe',
    output: 'jpeg',
    quality: 0.72,
    maxWidth: 1600,
    hint: 'Useful for forms, support uploads, and places that complain about file size.',
  },
  {
    id: 'keep-png',
    label: 'Clean PNG',
    output: 'png',
    quality: 0.92,
    maxWidth: 1800,
    hint: 'Best for screenshots, logos and flat graphics. PNG is shrunk by reducing it to a palette, which is the only thing that makes this format smaller.',
  },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
];

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// A canvas re-encode of a PNG is lossless, so on its own it hands back a file the
// same size or larger than the one that arrived: PNG has no quality dial to turn.
// The only thing that genuinely shrinks this format is reducing it to a palette,
// so the PNG path runs the resized frame through the same quantiser the dedicated
// PNG compressor uses. The quantiser is pulled in on demand, because it costs a
// wasm download that nobody exporting a JPG or a WebP should have to pay for.
async function shrinkPngFurther(encoded: Blob) {
  try {
    const { quantizePngWithPalette } = await import('@/lib/browser-gif-tools');
    const quantised = await quantizePngWithPalette({ file: encoded, maxColors: 256 });

    // On small flat images a palette can cost more than it saves, so the smaller
    // of the two is what gets handed back rather than whatever ran last.
    return quantised.size < encoded.size ? quantised : encoded;
  } catch {
    // Without the quantiser the plain re-encode is still a correct PNG. Losing a
    // saving is worth far less than failing a compression the visitor asked for.
    return encoded;
  }
}

function calculateTargetSize(width: number, height: number, maxWidth: number) {
  if (width <= maxWidth) {
    return { width, height };
  }

  const ratio = maxWidth / width;

  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function getSavingsPercent(sourceSize: number, resultSize: number) {
  if (!sourceSize || resultSize >= sourceSize) {
    return 0;
  }

  return Math.round(((sourceSize - resultSize) / sourceSize) * 100);
}

export default function BrowserImageCompressor() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [activePresetId, setActivePresetId] = useState(presets[0].id);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(presets[0].output);
  const [quality, setQuality] = useState(presets[0].quality);
  const [maxWidth, setMaxWidth] = useState<number>(presets[0].maxWidth);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [resultDimensions, setResultDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const activePreset = presets.find((preset) => preset.id === activePresetId) ?? null;
  const savingsPercent = useMemo(
    () => (loadedImage && resultSize ? getSavingsPercent(loadedImage.size, resultSize) : 0),
    [loadedImage, resultSize],
  );

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl, previewUrl]);

  function resetOutputState() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    setPreviewUrl(null);
    setDownloadUrl(null);
    setDownloadName(null);
    setResultSize(null);
    setResultDimensions(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      setLoadedImage(null);
      return;
    }

    try {
      const image = await loadImageFromFile(file);
      setLoadedImage({
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    } catch (error) {
      setLoadedImage(null);
      setErrorMessage(error instanceof Error ? error.message : 'Could not read this image.');
    }
  }

  function applyPreset(preset: CompressionPreset) {
    setActivePresetId(preset.id);
    setOutputFormat(preset.output);
    setQuality(preset.quality);
    setMaxWidth(preset.maxWidth);
    resetOutputState();
  }

  async function runCompression() {
    if (!loadedImage) {
      setErrorMessage('Choose an image first.');
      return;
    }

    setIsCompressing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'compression' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another compression right now.';
        throw new Error(message);
      }

      const nextSize = calculateTargetSize(loadedImage.width, loadedImage.height, maxWidth);
      const canvas = document.createElement('canvas');
      canvas.width = nextSize.width;
      canvas.height = nextSize.height;

      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare the image.');
      }

      context.drawImage(loadedImage.element, 0, 0, nextSize.width, nextSize.height);

      const encodedBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!encodedBlob) {
        throw new Error('The compression could not finish for this image.');
      }

      const blob =
        selectedOutput.value === 'png' ? await shrinkPngFurther(encodedBlob) : encodedBlob;

      // A PNG that is already well encoded can beat both a palette and a canvas
      // re-encode, and when it does, every path here produces something larger
      // than the file that arrived. Handing that back under a "compressed" label,
      // with a savings figure the tool would then have to invent, is worse than
      // admitting there was nothing to win. Only the same-format, same-size case
      // is refused: converting into PNG, or resizing, is allowed to grow the file
      // because that is what was asked for.
      const sourceWasPng = loadedImage.type === 'image/png' || /\.png$/i.test(loadedImage.name);
      const wasResized = nextSize.width !== loadedImage.width;

      if (
        selectedOutput.value === 'png' &&
        sourceWasPng &&
        !wasResized &&
        blob.size >= loadedImage.size
      ) {
        throw new Error(
          'This PNG is already smaller than a palette or a re-encode can make it, so it has been left alone rather than handed back larger. Reducing the width is the lever that still works on a file like this.',
        );
      }

      const nextPreviewUrl = URL.createObjectURL(blob);
      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextFileName = `${getBaseName(loadedImage.name)}-compressed.${selectedOutput.extension}`;

      setPreviewUrl(nextPreviewUrl);
      setDownloadUrl(nextDownloadUrl);
      setDownloadName(nextFileName);
      setResultSize(blob.size);
      setResultDimensions(nextSize);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : null,
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The image could not be compressed.',
      );
    } finally {
      setIsCompressing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Compress images in the browser</h2>
          <p>
            Reduce file size, keep the image usable, and download the lighter export right away.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Popular compression presets">
        {presets.map((preset) => (
          <button
            key={preset.id}
            className={`tool-live-route ${activePresetId === preset.id ? 'tool-live-route-active' : ''}`}
            onClick={() => applyPreset(preset)}
            type="button"
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{loadedImage ? loadedImage.name : 'Choose an image or drop it here'}</strong>
              <span>
                {loadedImage
                  ? `${formatFileSize(loadedImage.size)} • ${loadedImage.type || 'Image file'}`
                  : 'Works best with PNG, JPG, JPEG, WebP, GIF, and BMP files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>From</span>
              <input
                disabled
                readOnly
                type="text"
                value={
                  loadedImage ? normalizeSourceLabel(loadedImage.type) : 'Auto-detect after upload'
                }
              />
            </label>
            <label className="field">
              <span>Export as</span>
              <select
                onChange={(event) => {
                  setActivePresetId('custom');
                  setOutputFormat(event.target.value as OutputFormat);
                  resetOutputState();
                }}
                value={outputFormat}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-quality-shell tool-editor-sliders">
            <label className="field tool-live-quality">
              <span>Max width</span>
              <input
                max="3200"
                min="400"
                onChange={(event) => {
                  setActivePresetId('custom');
                  setMaxWidth(Math.max(400, Number(event.target.value) || 400));
                  resetOutputState();
                }}
                step="20"
                type="range"
                value={maxWidth}
              />
              <small>{`${maxWidth}px on the long side`}</small>
            </label>
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.95"
                min="0.4"
                onChange={(event) => {
                  setActivePresetId('custom');
                  setQuality(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `${Math.round(quality * 100)}% quality`
                  : 'PNG has no quality dial, so it is shrunk with a palette instead'}
              </small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!loadedImage || isCompressing}
              onClick={() => void runCompression()}
              type="button"
            >
              {isCompressing ? 'Compressing…' : `Compress to ${selectedOutput.label}`}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download {selectedOutput.label}
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Compression that stays readable</h3>
          <div className="tool-mockup-chip-list">
            <span>JPG</span>
            <span>WebP</span>
            <span>PNG</span>
            <span>Upload-safe</span>
          </div>
          <p>
            {activePreset?.hint ??
              'Use a preset to get close quickly, then adjust width and quality if needed.'}
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>Upload PNG, JPG, JPEG, WebP, GIF, or BMP. Export to JPG, WebP, or PNG.</span>
          </div>
          {loadedImage ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(loadedImage.size)}</span>
              </div>
              <div>
                <strong>Type</strong>
                <span>{normalizeSourceLabel(loadedImage.type)}</span>
              </div>
              <div>
                <strong>Resolution</strong>
                <span>{`${loadedImage.width} × ${loadedImage.height}`}</span>
              </div>
              <div>
                <strong>Export</strong>
                <span>{selectedOutput.label}</span>
              </div>
              <div>
                <strong>Max width</strong>
                <span>{`${maxWidth}px`}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Result</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              {savingsPercent > 0 ? (
                <div>
                  <strong>Savings</strong>
                  <span>{`${savingsPercent}% smaller`}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {loadedImage ? (
        <div className="tool-live-preview tool-compression-preview">
          <h3>Preview</h3>
          <div className="tool-compression-preview-grid">
            <div className="tool-live-preview-frame">
              <img alt="Original preview" src={loadedImage.element.src} />
            </div>
            <div className="tool-live-preview-frame">
              {previewUrl ? (
                <img alt="Compressed preview" src={previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Compressed preview</strong>
                  <span>Run the export to compare the result.</span>
                </div>
              )}
            </div>
          </div>
          {resultDimensions ? (
            <p>
              Result size: {resultDimensions.width} × {resultDimensions.height}
              {resultSize ? ` • ${formatFileSize(resultSize)}` : ''}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
